/**
 * Interceptor manager for handling request and response interceptors
 */

import type { IInterceptorManager } from '../types'

/**
 * Manages interceptors for requests and responses
 */
export class InterceptorManager<T> implements IInterceptorManager {
  private interceptors: Array<T | null> = []
  private nextId = 0

  /**
   * Adds an interceptor
   */
  use(onFulfilled?: any, onRejected?: any): number {
    const id = this.nextId++

    this.interceptors[id] = {
      onFulfilled,
      onRejected,
    } as T

    return id
  }

  /**
   * Removes an interceptor by ID
   */
  eject(id: number): void {
    if (this.interceptors[id]) {
      this.interceptors[id] = null
    }
  }

  /**
   * Clears all interceptors
   */
  clear(): void {
    this.interceptors = []
    this.nextId = 0
  }

  /**
   * Gets all active interceptors
   */
  getInterceptors(): T[] {
    return this.interceptors.filter((interceptor): interceptor is T => interceptor !== null)
  }

  /**
   * Gets the number of active interceptors
   */
  size(): number {
    return this.getInterceptors().length
  }

  /**
   * Checks if there are any interceptors
   */
  isEmpty(): boolean {
    return this.size() === 0
  }

  /**
   * Executes all interceptors in sequence
   */
  async executeAll<TInput, TOutput = TInput>(
    input: TInput,
    executor: (interceptor: T, input: TInput) => Promise<TOutput> | TOutput
  ): Promise<TOutput> {
    let result = input as unknown as TOutput

    for (const interceptor of this.getInterceptors()) {
      try {
        result = await Promise.resolve(executor(interceptor, result as unknown as TInput))
      } catch (error) {
        // If an interceptor throws, we still want to continue with the next one
        // The specific error handling should be done in the executor
        throw error
      }
    }

    return result
  }

  /**
   * Executes interceptors with error handling
   */
  async executeWithErrorHandling<TInput, TOutput = TInput>(
    input: TInput,
    onFulfilledExecutor: (interceptor: T, input: TInput) => Promise<TOutput> | TOutput,
    onRejectedExecutor?: (interceptor: T, error: any) => Promise<any> | any
  ): Promise<TOutput> {
    let result = input as unknown as TOutput
    let lastError: any = null

    for (const interceptor of this.getInterceptors()) {
      try {
        if (lastError && onRejectedExecutor) {
          // If there was an error and we have an error handler, use it
          lastError = await Promise.resolve(onRejectedExecutor(interceptor, lastError))
          if (lastError === null || lastError === undefined) {
            // Error was handled, continue with normal flow
            lastError = null
            continue
          }
        } else if (!lastError) {
          // Normal execution
          result = await Promise.resolve(
            onFulfilledExecutor(interceptor, result as unknown as TInput)
          )
        }
      } catch (error) {
        lastError = error
      }
    }

    if (lastError) {
      throw lastError
    }

    return result
  }

  /**
   * Creates a new interceptor manager with copied interceptors
   */
  clone(): InterceptorManager<T> {
    const cloned = new InterceptorManager<T>()
    cloned.interceptors = [...this.interceptors]
    cloned.nextId = this.nextId
    return cloned
  }

  /**
   * Merges interceptors from another manager
   */
  merge(other: InterceptorManager<T>): void {
    for (const interceptor of other.getInterceptors()) {
      this.interceptors.push(interceptor)
    }
  }

  /**
   * Filters interceptors based on a predicate
   */
  filter(predicate: (interceptor: T) => boolean): InterceptorManager<T> {
    const filtered = new InterceptorManager<T>()

    for (const interceptor of this.getInterceptors()) {
      if (predicate(interceptor)) {
        filtered.interceptors.push(interceptor)
      }
    }

    return filtered
  }

  /**
   * Maps interceptors to a new type
   */
  map<U>(mapper: (interceptor: T) => U): U[] {
    return this.getInterceptors().map(mapper)
  }

  /**
   * Finds an interceptor by predicate
   */
  find(predicate: (interceptor: T) => boolean): T | undefined {
    return this.getInterceptors().find(predicate)
  }

  /**
   * Checks if any interceptor matches the predicate
   */
  some(predicate: (interceptor: T) => boolean): boolean {
    return this.getInterceptors().some(predicate)
  }

  /**
   * Checks if all interceptors match the predicate
   */
  every(predicate: (interceptor: T) => boolean): boolean {
    return this.getInterceptors().every(predicate)
  }

  /**
   * Iterates over all interceptors
   */
  forEach(callback: (interceptor: T, index: number) => void): void {
    this.getInterceptors().forEach(callback)
  }

  /**
   * Converts to array
   */
  toArray(): T[] {
    return this.getInterceptors()
  }

  /**
   * Gets interceptor statistics
   */
  getStats(): {
    total: number
    active: number
    removed: number
  } {
    const active = this.getInterceptors().length
    const total = this.interceptors.length

    return {
      total,
      active,
      removed: total - active,
    }
  }
}
